/**
 * Vague 20 — Gestion des Vues (Settings → Vues)
 *
 * Page qui permet de :
 *  - Lister toutes les vues accessibles (système / org / perso)
 *  - Activer une vue (radio)
 *  - Dupliquer un preset en vue personnelle
 *  - Créer une nouvelle vue (form simple avec cases à cocher)
 *  - Éditer une vue perso/org (renommer + toggles modules + créer/supprimer groupes)
 *  - Supprimer une vue perso/org
 *
 * Le builder est volontairement minimaliste pour le MVP (cases à cocher + boutons),
 * pas de drag-and-drop. Suffisant pour décider ce qu'on voit et regroupements.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Trash2, Copy, Check, Edit3, Save, X as XIcon, Loader2, Layers, Eye, EyeOff,
  GripVertical, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useActiveView, MODULE_KEY_TO_LABEL } from "@/lib/useActiveView";

const ALL_MODULE_KEYS = Object.keys(MODULE_KEY_TO_LABEL);

export default function ViewsManager() {
  const navigate = useNavigate();
  const { view: activeView, setActiveView, refresh: refreshActive } = useActiveView();
  const [params] = useSearchParams();
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // view object being edited

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/views");
      setViews(r.data?.items || []);
    } catch (e) {
      notify.error(fmtError(e, "Vues indisponibles"));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- chargement unique au montage
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("new") === "1") {
      setEditing(newBlankView());
    }
  }, [params]);

  const activate = async (v) => {
    try {
      await setActiveView(v.id);
      notify.success(`Vue active : ${v.name}`);
    } catch (e) {
      notify.error(fmtError(e, "Activation échouée"));
    }
  };

  const duplicate = async (v) => {
    try {
      const r = await api.post(`/workspace/views/${v.id}/duplicate`);
      await load();
      setEditing(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Duplication échouée"));
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Supprimer la vue « ${v.name} » ?`)) return;
    try {
      await api.delete(`/workspace/views/${v.id}`);
      notify.success("Vue supprimée");
      await load();
      await refreshActive();
    } catch (e) {
      notify.error(fmtError(e, "Suppression échouée"));
    }
  };

  if (loading) {
    return <div className="text-zinc-500 text-sm py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>;
  }

  return (
    <div className="space-y-5" data-testid="views-manager-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#7F77DD]">
            <Layers className="w-3 h-3" /> Settings · Vues intelligentes
          </div>
          <h1 className="noam-display text-2xl font-bold mt-1">Gestion des vues</h1>
          <p className="text-zinc-400 text-xs mt-1 max-w-2xl">
            Personnalisez les modules visibles, leur ordre, leurs regroupements en onglets.
            Chaque vue est privée par défaut. Les presets système ne sont pas modifiables — dupliquez-les pour les adapter.
          </p>
        </div>
        <button
          onClick={() => setEditing(newBlankView())}
          className="btn-primary text-xs"
          data-testid="views-new-btn"
        >
          <Plus className="w-3 h-3" /> Nouvelle vue
        </button>
      </div>

      {!editing && (
        <div className="space-y-2" data-testid="views-list">
          {views.map((v) => {
            const isActive = v.id === activeView?.id;
            const editable = v.scope !== "system";
            return (
              <div
                key={v.id}
                className={`noam-card p-4 ${isActive ? "border-[#7F77DD]/40" : ""}`}
                data-testid={`views-item-${v.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-zinc-100">{v.name}</h3>
                      <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold"
                        style={{
                          background: v.scope === "system" ? "#7F77DD1A" : v.scope === "org" ? "#1D9E751A" : "#A78BFA1A",
                          color: v.scope === "system" ? "#7F77DD" : v.scope === "org" ? "#1D9E75" : "#A78BFA",
                        }}>
                        {v.scope === "system" ? "Système" : v.scope === "org" ? "Org" : "Perso"}
                      </span>
                      {isActive && <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold bg-[#7F77DD]/20 text-[#7F77DD]">Active</span>}
                      <span className="text-[10px] text-zinc-500">{(v.modules || []).filter((m) => m.visible !== false).length} modules visibles</span>
                    </div>
                    {v.description && <p className="text-xs text-zinc-400 mt-1">{v.description}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!isActive && (
                      <button onClick={() => activate(v)} className="text-xs px-2 py-1 rounded border border-[#7F77DD]/40 text-[#7F77DD] hover:bg-[#7F77DD]/10" data-testid={`views-activate-${v.id}`}>
                        Activer
                      </button>
                    )}
                    <button onClick={() => duplicate(v)} className="text-xs px-2 py-1 rounded border border-white/10 text-zinc-400 hover:text-zinc-200" title="Dupliquer" data-testid={`views-duplicate-${v.id}`}>
                      <Copy className="w-3 h-3" />
                    </button>
                    {editable && (
                      <>
                        <button onClick={() => setEditing(v)} className="text-xs px-2 py-1 rounded border border-white/10 text-zinc-400 hover:text-zinc-200" title="Éditer" data-testid={`views-edit-${v.id}`}>
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button onClick={() => remove(v)} className="text-xs px-2 py-1 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10" title="Supprimer" data-testid={`views-delete-${v.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <ViewEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); await refreshActive(); }}
        />
      )}
    </div>
  );
}

function newBlankView() {
  return {
    id: null,
    name: "Ma vue",
    description: "",
    scope: "personal",
    modules: ALL_MODULE_KEYS.map((key, i) => ({ key, visible: true, order: i })),
  };
}

function ViewEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial.name || "");
  const [description, setDescription] = useState(initial.description || "");
  const [modules, setModules] = useState(initial.modules || []);
  const [saving, setSaving] = useState(false);
  // V20.2 — scope choisi par l'utilisateur (perso ou org partagée) lors de la création.
  // À l'édition, le scope est fixé (initial.scope) et non modifiable.
  const [scope, setScope] = useState(initial.scope || "personal");
  // Permet de savoir si l'user courant peut créer une vue org (= admin/super_admin/ultra_admin)
  const [canShareOrg, setCanShareOrg] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        const u = r.data?.user || r.data || {};
        setCanShareOrg(
          u.role === "admin" || u.role === "super_admin" ||
          u.is_super_admin === true || u.is_ultra_admin === true,
        );
      } catch (e) { console.debug("auth/me indisponible pour canShareOrg", e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exécution unique au montage
  }, []);
  // V20.2 — drag & drop natif HTML5. dragIndex = index source, hoverIndex = index cible
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    setModules((prev) => {
      const arr = [...prev];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr.map((it, i) => ({ ...it, order: i }));
    });
  };

  // Ensure all ALL_MODULE_KEYS are in modules list (init missing ones as hidden)
  useEffect(() => {
    setModules((prev) => {
      const existing = new Set(prev.map((m) => m.key));
      const groupKeys = new Set(prev.filter((m) => m.is_group).flatMap((g) => (g.children || []).map((c) => c.key)));
      const next = [...prev];
      for (const k of ALL_MODULE_KEYS) {
        if (!existing.has(k) && !groupKeys.has(k)) {
          next.push({ key: k, visible: false, order: next.length });
        }
      }
      return next;
    });
  }, []);

  const visibleCount = useMemo(() =>
    modules.filter((m) => m.visible !== false).reduce((sum, m) => sum + (m.is_group ? (m.children || []).filter((c) => c.visible !== false).length : 1), 0),
    [modules]
  );

  const toggleModule = (key) => {
    setModules((prev) => prev.map((m) => m.key === key ? { ...m, visible: !m.visible } : m));
  };
  const toggleGroupChild = (groupKey, childKey) => {
    setModules((prev) => prev.map((m) => m.key === groupKey ? {
      ...m, children: (m.children || []).map((c) => c.key === childKey ? { ...c, visible: !c.visible } : c)
    } : m));
  };

  const save = async () => {
    if (!name.trim()) { notify.error("Nom requis"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), modules };
      if (initial.id) {
        await api.patch(`/workspace/views/${initial.id}`, payload);
        notify.success("Vue mise à jour");
      } else {
        // V20.2 — scope choisi par l'user (perso ou partagée org si admin)
        payload.scope = (scope === "org" && canShareOrg) ? "org" : "personal";
        await api.post("/workspace/views", payload);
        notify.success(payload.scope === "org" ? "Vue partagée créée (visible par tous les membres)" : "Vue personnelle créée");
      }
      onSaved();
    } catch (e) {
      notify.error(fmtError(e, "Sauvegarde échouée"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="noam-card p-5 space-y-4" data-testid="views-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-zinc-100">
          {initial.id ? "Éditer la vue" : "Nouvelle vue"}
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border border-white/10 text-zinc-400 rounded" data-testid="views-editor-cancel">
            Annuler
          </button>
          <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="views-editor-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Sauvegarder
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="noam-input text-sm w-full mt-1" data-testid="views-editor-name" />
        </div>
        <div className="bg-white/[0.02] border border-white/5 rounded p-3 flex items-center justify-center">
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-wider text-zinc-500">Modules visibles</div>
            <div className="text-xl font-bold text-[#7F77DD] mt-0.5" data-testid="views-editor-counter">{visibleCount}</div>
          </div>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="noam-input text-xs w-full mt-1" data-testid="views-editor-description" />
      </div>

      {/* V20.2 — Scope picker (uniquement à la création, admin only pour "org") */}
      {!initial.id && (
        <div data-testid="views-editor-scope-picker">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Visibilité</label>
          <div className="flex items-center gap-2 mt-1.5">
            <button
              type="button"
              onClick={() => setScope("personal")}
              className={`text-xs px-3 py-1.5 rounded border transition ${scope === "personal" ? "border-[#7F77DD] bg-[#7F77DD]/15 text-[#7F77DD]" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
              data-testid="views-editor-scope-personal"
            >
              Personnelle — visible par moi seul
            </button>
            <button
              type="button"
              disabled={!canShareOrg}
              onClick={() => setScope("org")}
              className={`text-xs px-3 py-1.5 rounded border transition ${scope === "org" ? "border-[#1D9E75] bg-[#1D9E75]/15 text-[#1D9E75]" : "border-white/10 text-zinc-400 hover:bg-white/5"} disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid="views-editor-scope-org"
              title={canShareOrg ? "Partager avec tous les membres de l'organisation" : "Réservé aux admins"}
            >
              Partagée org — visible par toute l'organisation
              {!canShareOrg && <span className="ml-1 text-[9px]">(admin only)</span>}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-1.5" data-testid="views-editor-modules">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Modules de la vue</div>
          <div className="text-[9px] text-zinc-600 italic flex items-center gap-1">
            <GripVertical className="w-3 h-3" /> Glisser pour réordonner
          </div>
        </div>
        {modules.map((m, idx) => {
          const isDragTarget = hoverIndex === idx && dragIndex !== null && dragIndex !== idx;
          const isDragging = dragIndex === idx;
          const dragProps = {
            draggable: true,
            onDragStart: (e) => {
              setDragIndex(idx);
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", String(idx));
            },
            onDragOver: (e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (hoverIndex !== idx) setHoverIndex(idx);
            },
            onDragLeave: () => {
              if (hoverIndex === idx) setHoverIndex(null);
            },
            onDrop: (e) => {
              e.preventDefault();
              const from = Number(e.dataTransfer.getData("text/plain"));
              if (!Number.isNaN(from)) reorder(from, idx);
              setDragIndex(null);
              setHoverIndex(null);
            },
            onDragEnd: () => { setDragIndex(null); setHoverIndex(null); },
          };
          const dragClass = isDragging ? "opacity-30" : (isDragTarget ? "ring-2 ring-[#7F77DD]/60" : "");
          if (m.is_group) {
            return (
              <div key={m.key} {...dragProps} className={`border border-[#7F77DD]/30 bg-[#7F77DD]/5 rounded p-2.5 transition ${dragClass}`} data-testid={`views-editor-group-${m.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <GripVertical className="w-3 h-3 text-[#7F77DD]/60 cursor-grab active:cursor-grabbing flex-shrink-0" />
                  <button onClick={() => toggleModule(m.key)} className="flex-shrink-0">
                    {m.visible !== false ? <Eye className="w-3.5 h-3.5 text-[#7F77DD]" /> : <EyeOff className="w-3.5 h-3.5 text-zinc-600" />}
                  </button>
                  <Layers className="w-3 h-3 text-[#7F77DD] flex-shrink-0" />
                  <span className="text-xs font-medium text-zinc-200 flex-1">{m.label || m.key}</span>
                  <span className="text-[9px] text-zinc-500">groupe · {(m.children || []).length} onglets</span>
                </div>
                <div className="ml-6 space-y-1 border-l border-white/10 pl-3">
                  {(m.children || []).map((c) => (
                    <div key={c.key} className="flex items-center gap-2" data-testid={`views-editor-child-${m.key}-${c.key}`}>
                      <button onClick={() => toggleGroupChild(m.key, c.key)} className="flex-shrink-0">
                        {c.visible !== false ? <Eye className="w-3 h-3 text-[#1D9E75]" /> : <EyeOff className="w-3 h-3 text-zinc-600" />}
                      </button>
                      <ChevronRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                      <span className="text-xs text-zinc-300 flex-1">{c.tab_label || MODULE_KEY_TO_LABEL[c.key] || c.key}</span>
                      <span className="text-[9px] text-zinc-600">{c.key}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          return (
            <div key={m.key} {...dragProps} className={`flex items-center gap-2 p-2 rounded transition ${m.visible !== false ? "bg-white/[0.02]" : "opacity-50"} ${dragClass}`} data-testid={`views-editor-module-${m.key}`}>
              <GripVertical className="w-3 h-3 text-zinc-700 cursor-grab active:cursor-grabbing flex-shrink-0" />
              <button onClick={() => toggleModule(m.key)} className="flex-shrink-0">
                {m.visible !== false ? <Eye className="w-3.5 h-3.5 text-[#1D9E75]" /> : <EyeOff className="w-3.5 h-3.5 text-zinc-600" />}
              </button>
              <span className="text-xs text-zinc-300 flex-1">{MODULE_KEY_TO_LABEL[m.key] || m.key}</span>
              <span className="text-[9px] text-zinc-600">{m.key}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
